/**
 * Extract a human-readable message from a failed request.
 *
 * Why this exists: never render `error.message` from a graphql-request failure.
 * `ClientError` builds its message as
 *
 *   `${firstErrorMessage}: ${JSON.stringify({ response, request })}`
 *
 * so `error.message` is the real message followed by the entire response *and* the
 * request — the GraphQL document, its fragments, the submitted variables, and (outside
 * production) the backend stacktrace with absolute file paths. Toasting it produced
 * ~1.3 KB of JSON where a sentence belonged.
 *
 * The correct source is always `error.response.errors[0].message`. `error.response.data`
 * is an axios shape and is always null on a GraphQL error, which is why the old
 * `error?.response?.data?.message || error?.message` chain fell through to the JSON
 * every single time.
 */

/** Error codes the backend sends in `extensions.code`. Keep in sync with the backend. */
export const ERROR_CODES = {
  TRIAL_EXPIRED: 'SUBSCRIPTION_TRIAL_EXPIRED',
  FREE_TRIAL_ORGANIZATION_LIMIT_REACHED:
    'FREE_TRIAL_ORGANIZATION_LIMIT_REACHED',
  NO_SEATS_AVAILABLE: 'NO_SEATS_AVAILABLE',
} as const;

const firstGraphQLError = (error: any) => error?.response?.errors?.[0];

/**
 * The message to show the user.
 *
 * Falls back to `fallback` rather than to `error.message`: an unrecognised error shape
 * means we do not know what is in there, and a generic sentence beats a JSON dump.
 */
export const getErrorMessage = (
  error: any,
  fallback = 'Something went wrong',
): string => {
  const graphQLMessage = firstGraphQLError(error)?.message;
  if (typeof graphQLMessage === 'string' && graphQLMessage.trim()) {
    return graphQLMessage;
  }

  // REST endpoints (auth, uploads) genuinely use this axios shape.
  const restMessage = error?.response?.data?.message;
  if (typeof restMessage === 'string' && restMessage.trim()) {
    return restMessage;
  }

  // A network failure never reached the server, so it has no response and its
  // `message` is a plain string ("Failed to fetch") rather than a JSON dump.
  if (
    !error?.response &&
    typeof error?.message === 'string' &&
    error.message.trim()
  ) {
    return error.message;
  }

  return fallback;
};

/** The backend's `extensions.code`, or undefined when the error carries none. */
export const getErrorCode = (error: any): string | undefined => {
  const code = firstGraphQLError(error)?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
};

/** True when the write was refused because the organization's trial has lapsed. */
export const isTrialExpiredError = (error: any): boolean =>
  getErrorCode(error) === ERROR_CODES.TRIAL_EXPIRED;

/**
 * True when a server-side seat guard (sprint-20, story-092) refused the write because the
 * organization has no free seat left. Routes to the same NoSeatsModal/AddAddonModal flow the
 * client-side pre-check already opens, instead of falling through to a generic error toast.
 */
export const isNoSeatsAvailableError = (error: any): boolean =>
  getErrorCode(error) === ERROR_CODES.NO_SEATS_AVAILABLE;

/**
 * Sprint 25, story-128 (run 5). Client create/update's "refuse-whole-operation" cases
 * (`ClientService.isRefuseWholeOperationError`) carry no `extensions.code`, only a message —
 * matched by substring against the exact backend strings (`user.service.ts` /
 * `client.service.ts`), the same way this file already matches other plain-`Error` cases. Used
 * to show the two UX-doc inline-under-Email errors instead of a generic toast.
 */
export const isClientEmailAlreadyMemberError = (error: any): boolean =>
  (firstGraphQLError(error)?.message || '').includes(
    'already belongs to a member of this organization with a different role',
  );

export const isClientEmailAlreadyLinkedError = (error: any): boolean =>
  (firstGraphQLError(error)?.message || '').includes(
    'already linked to another client in this organization',
  );

/**
 * `occupiedSeats`/`includedSeats` off a `NO_SEATS_AVAILABLE` error's `extensions`, so the
 * fallback modal can show accurate numbers without a second network round trip. Returns null
 * when the error doesn't carry both fields (e.g. any other error code).
 */
export const getNoSeatsExtensions = (
  error: any,
): { occupiedSeats: number; includedSeats: number } | null => {
  const extensions = firstGraphQLError(error)?.extensions;
  if (
    typeof extensions?.occupiedSeats === 'number' &&
    typeof extensions?.includedSeats === 'number'
  ) {
    return {
      occupiedSeats: extensions.occupiedSeats,
      includedSeats: extensions.includedSeats,
    };
  }
  return null;
};
